import React from 'react';
import clsx from 'clsx';
import { func, instanceOf, object, shape } from 'prop-types';

import TextField from '../TextField';
import DatePicker from 'react-datepicker';
import datePickerProps from './datePickerProps';

import 'react-datepicker/dist/react-datepicker.css';
import defaultClasses from './DateField.module.css';

/**
 * The `react-datepicker` input component has many dozens of props that it
 * supports. This function separates out those props from those that should be
 * spread on to the top-level form field.
 */
function extractInputProps({ inputProps = {}, ...props }) {
    const fieldProps = {};
    const extractedInputProps = {};
    for (let [key, value] of Object.entries(props)) {
        if (datePickerProps[key]) {
            extractedInputProps[key] = inputProps[key] || value;
        } else {
            fieldProps[key] = value;
        }
    }
    return {
        fieldProps,
        inputProps: extractedInputProps
    };
}

const CustomDatePicker = ({
    inputRef,
    ...props
}) => {
    return (
        <DatePicker
            {...props}
            open={props.open}
            ref={ref => {
                inputRef(ref ? ref.input : null);
            }}
        />
    );
};

const DateField = ({
    InputLabelProps,
    InputProps,
    value,
    ...props
}) => {
    const { fieldProps, inputProps, disabled } = extractInputProps(props);
    return (
        <TextField
            InputLabelProps={InputLabelProps}
            InputProps={{
                inputComponent:   CustomDatePicker,
                disableUnderline: props.disableUnderline,
                ...InputProps,
                className:        clsx(
                    defaultClasses.inputBase,
                    InputProps.className
                )
            }}
            /* eslint-disable react/jsx-no-duplicate-props */
            inputProps={{
                disabled:  disabled,
                selected:  value,
                ...inputProps,
                className: clsx(
                    defaultClasses.input,
                    inputProps.input
                ),
                dayClassName: (date) =>
                    clsx(
                        defaultClasses.day,
                        inputProps.dayClassName && inputProps.dayClassName(date)
                    ),
                popperClassName: clsx(
                    defaultClasses.popper,
                    inputProps.popperClassName,
                    InputProps.popperoverride
                )
            }}
            /* eslint-enable react/jsx-no-duplicate-props */
            type="datetime"
            {...fieldProps}
        />
    );
};

DateField.defaultProps = {
    InputLabelProps: {},
    InputProps:      {},
    inputProps:      {}
};

DateField.propTypes = {
    InputLabelProps: object,
    InputProps:      object,
    inputProps:      shape({
        dayClassName: func
    }),
    value: instanceOf(Date)
};

export default DateField;
